import { __extends } from "tslib";
import { Vector3 } from "../../Maths/math.vector";
import { Scalar } from "../../Maths/math.scalar";
import { DeepCopier } from "../../Misc/deepCopier";
/**
 * Particle emitter emitting particles from the inside of a cylinder.
 * It emits the particles alongside the cylinder radius. The emission direction might be randomized.
 */
var CylinderParticleEmitter = /** @class */ (function () {
    /**
    * Creates a new instance CylinderParticleEmitter
    * @param radius the radius of the emission cylinder (1 by default)
    * @param height the height of the emission cylinder (1 by default)
    * @param radiusRange the range of the emission cylinder [0-1] 0 Surface only, 1 Entire Radius (1 by default)
    * @param directionRandomizer defines how much to randomize the particle direction [0-1]
    */
    function CylinderParticleEmitter(
    /**
     * The radius of the emission cylinder.
     */
    radius, 
    /**
     * The height of the emission cylinder.
     */
    height, 
    /**
     * The range of emission [0-1] 0 Surface only, 1 Entire Radius.
     */
    radiusRange, 
    /**
     * How much to randomize the particle direction [0-1].
     */
    directionRandomizer) {
        if (radius === void 0) { radius = 1; }
        if (height === void 0) { height = 1; }
        if (radiusRange === void 0) { radiusRange = 1; }
        if (directionRandomizer === void 0) { directionRandomizer = 0; }
        this.radius = radius;
        this.height = height;
        this.radiusRange = radiusRange;
        this.directionRandomizer = directionRandomizer;
    }
    /**
     * Called by the particle System when the direction is computed for the created particle.
     * @param worldMatrix is the world matrix of the particle system
     * @param directionToUpdate is the direction vector to update with the result
     * @param particle is the particle we are computed the direction for
     * @param isLocal defines if the direction should be set in local space
     */
    CylinderParticleEmitter.prototype.startDirectionFunction = function (worldMatrix, directionToUpdate, particle, isLocal) {
        var direction = particle.position.subtract(worldMatrix.getTranslation()).normalize();
        var randY = Scalar.RandomRange(-this.directionRandomizer / 2, this.directionRandomizer / 2);
        var angle = Math.atan2(direction.x, direction.z);
        angle += Scalar.RandomRange(-Math.PI / 2, Math.PI / 2) * this.directionRandomizer;
        direction.y = randY; // set direction y to rand y to mirror normal of cylinder surface
        direction.x = Math.sin(angle);
        direction.z = Math.cos(angle);
        direction.normalize();
        if (isLocal) {
            directionToUpdate.copyFrom(direction);
            return;
        }
        Vector3.TransformNormalFromFloatsToRef(direction.x, direction.y, direction.z, worldMatrix, directionToUpdate);
    };
    /**
     * Called by the particle System when the position is computed for the created particle.
     * @param worldMatrix is the world matrix of the particle system
     * @param positionToUpdate is the position vector to update with the result
     * @param particle is the particle we are computed the position for
     * @param isLocal defines if the position should be set in local space
     */
    CylinderParticleEmitter.prototype.startPositionFunction = function (worldMatrix, positionToUpdate, particle, isLocal) {
        var yPos = Scalar.RandomRange(-this.height / 2, this.height / 2);
        var angle = Scalar.RandomRange(0, 2 * Math.PI);
        // Pick a properly distributed point within the circle https://programming.guide/random-point-within-circle.html
        var radiusDistribution = Scalar.RandomRange((1 - this.radiusRange) * (1 - this.radiusRange), 1);
        var positionRadius = Math.sqrt(radiusDistribution) * this.radius;
        var xPos = positionRadius * Math.cos(angle);
        var zPos = positionRadius * Math.sin(angle);
        if (isLocal) {
            positionToUpdate.copyFromFloats(xPos, yPos, zPos);
            return;
        }
        Vector3.TransformCoordinatesFromFloatsToRef(xPos, yPos, zPos, worldMatrix, positionToUpdate);
    };
    /**
     * Clones the current emitter and returns a copy of it
     * @returns the new emitter
     */
    CylinderParticleEmitter.prototype.clone = function () {
        var newOne = new CylinderParticleEmitter(this.radius, this.directionRandomizer);
        DeepCopier.DeepCopy(this, newOne);
        return newOne;
    };
    /**
     * Called by the GPUParticleSystem to setup the update shader
     * @param effect defines the update shader
     */
    CylinderParticleEmitter.prototype.applyToShader = function (effect) {
        effect.setFloat("radius", this.radius);
        effect.setFloat("height", this.height);
        effect.setFloat("radiusRange", this.radiusRange);
        effect.setFloat("directionRandomizer", this.directionRandomizer);
    };
    /**
     * Returns a string to use to update the GPU particles update shader
     * @returns a string containng the defines string
     */
    CylinderParticleEmitter.prototype.getEffectDefines = function () {
        return "#define CYLINDEREMITTER";
    };
    /**
     * Returns the string "CylinderParticleEmitter"
     * @returns a string containing the class name
     */
    CylinderParticleEmitter.prototype.getClassName = function () {
        return "CylinderParticleEmitter";
    };
    /**
     * Serializes the particle system to a JSON object.
     * @returns the JSON object
     */
    CylinderParticleEmitter.prototype.serialize = function () {
        var serializationObject = {};
        serializationObject.type = this.getClassName();
        serializationObject.radius = this.radius;
        serializationObject.height = this.height;
        serializationObject.radiusRange = this.radiusRange;
        serializationObject.directionRandomizer = this.directionRandomizer;
        return serializationObject;
    };
    /**
     * Parse properties from a JSON object
     * @param serializationObject defines the JSON object
     */
    CylinderParticleEmitter.prototype.parse = function (serializationObject) {
        this.radius = serializationObject.radius;
        this.height = serializationObject.height;
        this.radiusRange = serializationObject.radiusRange;
        this.directionRandomizer = serializationObject.directionRandomizer;
    };
    return CylinderParticleEmitter;
}());
export { CylinderParticleEmitter };
/**
 * Particle emitter emitting particles from the inside of a cylinder.
 * It emits the particles randomly between two vectors.
 */
var CylinderDirectedParticleEmitter = /** @class */ (function (_super) {
    __extends(CylinderDirectedParticleEmitter, _super);
    /**
     * Creates a new instance CylinderDirectedParticleEmitter
     * @param radius the radius of the emission cylinder (1 by default)
     * @param height the height of the emission cylinder (1 by default)
     * @param radiusRange the range of the emission cylinder [0-1] 0 Surface only, 1 Entire Radius (1 by default)
     * @param direction1 the min limit of the emission direction (up vector by default)
     * @param direction2 the max limit of the emission direction (up vector by default)
     */
    function CylinderDirectedParticleEmitter(radius, height, radiusRange, 
    /**
     * The min limit of the emission direction.
     */
    direction1, 
    /**
     * The max limit of the emission direction.
     */
    direction2) {
        if (radius === void 0) { radius = 1; }
        if (height === void 0) { height = 1; }
        if (radiusRange === void 0) { radiusRange = 1; }
        if (direction1 === void 0) { direction1 = new Vector3(0, 1, 0); }
        if (direction2 === void 0) { direction2 = new Vector3(0, 1, 0); }
        var _this = _super.call(this, radius, height, radiusRange) || this;
        _this.direction1 = direction1;
        _this.direction2 = direction2;
        return _this;
    }
    /**
     * Called by the particle System when the direction is computed for the created particle.
     * @param worldMatrix is the world matrix of the particle system
     * @param directionToUpdate is the direction vector to update with the result
     * @param particle is the particle we are computed the direction for
     */
    CylinderDirectedParticleEmitter.prototype.startDirectionFunction = function (worldMatrix, directionToUpdate, particle) {
        var randX = Scalar.RandomRange(this.direction1.x, this.direction2.x);
        var randY = Scalar.RandomRange(this.direction1.y, this.direction2.y);
        var randZ = Scalar.RandomRange(this.direction1.z, this.direction2.z);
        Vector3.TransformNormalFromFloatsToRef(randX, randY, randZ, worldMatrix, directionToUpdate);
    };
    /**
     * Clones the current emitter and returns a copy of it
     * @returns the new emitter
     */
    CylinderDirectedParticleEmitter.prototype.clone = function () {
        var newOne = new CylinderDirectedParticleEmitter(this.radius, this.height, this.radiusRange, this.direction1, this.direction2);
        DeepCopier.DeepCopy(this, newOne);
        return newOne;
    };
    /**
     * Called by the GPUParticleSystem to setup the update shader
     * @param effect defines the update shader
     */
    CylinderDirectedParticleEmitter.prototype.applyToShader = function (effect) {
        effect.setFloat("radius", this.radius);
        effect.setFloat("height", this.height);
        effect.setFloat("radiusRange", this.radiusRange);
        effect.setVector3("direction1", this.direction1);
        effect.setVector3("direction2", this.direction2);
    };
    /**
     * Returns a string to use to update the GPU particles update shader
     * @returns a string containng the defines string
     */
    CylinderDirectedParticleEmitter.prototype.getEffectDefines = function () {
        return "#define CYLINDEREMITTER\n#define DIRECTEDCYLINDEREMITTER";
    };
    /**
     * Returns the string "CylinderDirectedParticleEmitter"
     * @returns a string containing the class name
     */
    CylinderDirectedParticleEmitter.prototype.getClassName = function () {
        return "CylinderDirectedParticleEmitter";
    };
    /**
     * Serializes the particle system to a JSON object.
     * @returns the JSON object
     */
    CylinderDirectedParticleEmitter.prototype.serialize = function () {
        var serializationObject = _super.prototype.serialize.call(this);
        serializationObject.direction1 = this.direction1.asArray();
        serializationObject.direction2 = this.direction2.asArray();
        return serializationObject;
    };
    /**
     * Parse properties from a JSON object
     * @param serializationObject defines the JSON object
     */
    CylinderDirectedParticleEmitter.prototype.parse = function (serializationObject) {
        _super.prototype.parse.call(this, serializationObject);
        this.direction1.copyFrom(serializationObject.direction1);
        this.direction2.copyFrom(serializationObject.direction2);
    };
    return CylinderDirectedParticleEmitter;
}(CylinderParticleEmitter));
export { CylinderDirectedParticleEmitter };
//# sourceMappingURL=cylinderParticleEmitter.js.map